import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, forkJoin, map, switchMap, of, tap } from 'rxjs';
import {
  addGuestWishlistItem,
  addGuestWishlistItemFailure,
  addGuestWishlistItemSuccess,
  getGuestWishlist,
  getGuestWishlistFailure,
  getGuestWishlistSuccess,
  removeGuestWishlistItem,
  removeGuestWishlistItemFailure,
  removeGuestWishlistItemSuccess,
} from './actions';
import { WishlistItem } from 'src/app/shared/models';
import { UserService } from 'src/app/services/common-services/user.service';
import { CustomSnackbarComponent } from 'src/app/shared/components/custom-snackbar/custom-snackbar.component';

@Injectable()
export class GuestWishlistEffects {
  constructor(
    private actions$: Actions,
    private userService: UserService,
    private snackBar: MatSnackBar
  ) {}

  // LOAD WISHLIST
  getGuestWishlist$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getGuestWishlist),
      switchMap(() =>
        this.userService.getWishlistPaged({ search: '' }).pipe(
          switchMap((res: any) => {
            const rawItems = Array.isArray(res?.result) ? res.result : [];
            if (!rawItems.length) {
              return of(getGuestWishlistSuccess({ items: [] }));
            }

            return forkJoin(
              rawItems.map((r: any) =>
                this.userService.getPropertyById(r.propertyProfileId).pipe(
                  map((p: any) => {
                    const pr = p?.result ?? {};
                    return {
                      id: r.id,
                      propertyId: r.propertyProfileId,
                      title: r.name,
                      thumbnail: pr.thumbnail || pr.propertyPicture?.[0]?.path || '',
                      pricePerNight: pr.pricePerNight,
                      city: pr.location?.city,
                      country: pr.location?.country,
                      images: (pr.propertyPicture || []).slice(0, 4).map((pp: any) => pp?.path).filter(Boolean),
                    } as WishlistItem;
                  }),
                  catchError(() =>
                    of({
                      id: r.id,
                      propertyId: r.propertyProfileId,
                      title: r.name,
                    } as WishlistItem)
                  )
                )
              )
            ).pipe(map((items: WishlistItem[]) => getGuestWishlistSuccess({ items })));
          }),
          catchError((error) =>
            of(
              getGuestWishlistFailure({
                error: error?.error?.message || 'Failed to load guest wishlist',
              })
            )
          )
        )
      )
    )
  );

  // ADD ITEM
  addGuestWishlistItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addGuestWishlistItem),
      switchMap(({ propertyId }) =>
        this.userService.addWishlistItem(propertyId).pipe(
          tap(() => {
            this.snackBar.openFromComponent(CustomSnackbarComponent, {
              data: { title: 'Successfully added to Wishlist.', type: 'success' },
              panelClass: 'success',
              duration: 4000,
              horizontalPosition: 'start',
            });
          }),
          switchMap(() =>
            this.userService.getPropertyById(propertyId).pipe(
              map((p: any) => {
                const pr = p?.result ?? {};
                const item: WishlistItem = {
                  id: propertyId,
                  propertyId,
                  title: pr.title,
                  thumbnail: pr.thumbnail || pr.propertyPicture?.[0]?.path || '',
                  pricePerNight: pr.pricePerNight,
                  city: pr.location?.city,
                  country: pr.location?.country,
                  images: (pr.propertyPicture || []).slice(0, 4).map((pp: any) => pp?.path).filter(Boolean),
                };
                return addGuestWishlistItemSuccess({ item });
              }),
              catchError(() =>
                of(addGuestWishlistItemSuccess({ item: { id: propertyId, propertyId } as WishlistItem }))
              )
            )
          ),
          catchError((error) =>
            of(addGuestWishlistItemFailure({ propertyId, error: error?.error?.message || 'Failed to add item' }))
          )
        )
      )
    )
  );

  // REMOVE ITEM
  removeGuestWishlistItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(removeGuestWishlistItem),
      switchMap(({ propertyId }) =>
        this.userService.deleteWishlistItem(propertyId).pipe(
          tap(() => {
            this.snackBar.openFromComponent(CustomSnackbarComponent, {
              data: { title: 'Successfully removed from Wishlist.', type: 'success' },
              panelClass: 'success',
              duration: 4000,
              horizontalPosition: 'start',
            });
          }),
          map(() => removeGuestWishlistItemSuccess({ propertyId })),
          catchError((error) =>
            of(removeGuestWishlistItemFailure({ propertyId, error: error?.error?.message || 'Failed to remove item' }))
          )
        )
      )
    )
  );
}
